import { Link } from 'react-router-dom';
import { MapPin, Clock, Star, Users, Heart, ArrowRight, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { formatPrice, categoryColors, cn } from '../../lib/utils';
import { useState } from 'react';

// Tour Card Component
function TourPackageCard({ tour, onEnquire }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const discountPercent = tour.discounted_price
    ? Math.round(((tour.price - tour.discounted_price) / tour.price) * 100)
    : 0;

  return (
    <div
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`tour-card-${tour.slug}`}
    >
      {/* Image Container */}
      <div className="relative h-56 overflow-hidden">
        <img
          src={tour.images?.[0] || 'https://images.pexels.com/photos/2325446/pexels-photo-2325446.jpeg'}
          alt={tour.name}
          className={cn(
            'w-full h-full object-cover transition-transform duration-500',
            isHovered ? 'scale-110' : 'scale-100'
          )}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Top Badges */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          <div className="flex flex-col gap-2">
            <Badge className={cn(categoryColors[tour.category] || 'bg-gray-100', 'shadow-sm')}>
              {tour.category}
            </Badge>
            {tour.featured && (
              <Badge className="bg-[#FF6B35] text-white shadow-sm">
                🔥 Featured
              </Badge>
            )}
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              setIsLiked(!isLiked);
            }}
            className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
          >
            <Heart
              className={cn(
                'w-5 h-5 transition-colors',
                isLiked ? 'fill-red-500 text-red-500' : 'text-[#4A4A4A]'
              )}
            />
          </button>
        </div>

        {/* Discount Badge */}
        {discountPercent > 0 && (
          <div className="absolute bottom-4 left-4">
            <Badge className="bg-[#2A9D8F] text-white shadow-sm text-sm px-3">
              {discountPercent}% OFF
            </Badge>
          </div>
        )}

        {/* Price Tag */}
        <div className="absolute bottom-4 right-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg">
            {tour.discounted_price && (
              <span className="text-xs text-[#4A4A4A] line-through block">
                {formatPrice(tour.price)}
              </span>
            )}
            <span className="text-xl font-bold text-[#004E64]">
              {formatPrice(tour.discounted_price || tour.price)}
            </span>
            <span className="text-xs text-[#4A4A4A]">/person</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Location */}
        <div className="flex items-center gap-1 text-[#FF6B35] text-sm mb-2">
          <MapPin className="w-4 h-4" />
          <span className="font-medium">{tour.destination}</span>
        </div>

        {/* Title */}
        <h3
          className="text-lg font-semibold text-[#1A1A1A] mb-3 group-hover:text-[#FF6B35] transition-colors line-clamp-2 min-h-[56px]"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          {tour.name}
        </h3>

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-sm text-[#4A4A4A] mb-4">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{tour.duration_days}D/{tour.duration_nights}N</span>
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-[#E9C46A] text-[#E9C46A]" />
            <span>{tour.rating || '4.8'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{tour.max_group_size || '20'} Max</span>
          </div>
        </div>

        {/* Highlights */}
        {tour.highlights && tour.highlights.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tour.highlights.slice(0, 3).map((highlight, idx) => (
              <span
                key={idx}
                className="text-xs bg-gray-100 text-[#4A4A4A] px-2 py-1 rounded-full"
              >
                {highlight}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <Link to={`/tours/${tour.slug}`} className="flex-1">
            <Button
              variant="outline"
              className="w-full rounded-xl border-[#004E64] text-[#004E64] hover:bg-[#004E64] hover:text-white group/btn"
            >
              View Details
              <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <Button
            onClick={() => onEnquire(tour)}
            className="flex-1 rounded-xl bg-[#FF6B35] hover:bg-[#E85A2D] text-white"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Enquire
          </Button>
        </div>
      </div>
    </div>
  );
}

// Loading Skeleton
function PackageSkeleton() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
      <Skeleton className="h-56 w-full" />
      <div className="p-5 space-y-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-3/4" />
        <div className="flex gap-4">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="flex gap-3 pt-4">
          <Skeleton className="h-10 flex-1 rounded-xl" />
          <Skeleton className="h-10 flex-1 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

// Empty State
function EmptyState({ onClearFilters }) {
  return (
    <div className="col-span-full text-center py-16 bg-white rounded-2xl border border-gray-100">
      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <MapPin className="w-10 h-10 text-[#4A4A4A]" />
      </div>
      <h3 
        className="text-xl font-semibold text-[#1A1A1A] mb-2"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        No tours found
      </h3>
      <p className="text-[#4A4A4A] mb-6 max-w-md mx-auto">
        We couldn&apos;t find any tours matching your criteria. Try adjusting your filters or search for something else.
      </p>
      <Button
        onClick={onClearFilters}
        className="bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full px-8"
      >
        Clear All Filters
      </Button>
    </div>
  );
}

// Main PackageGrid Component
export function PackageGrid({ 
  tours, 
  loading, 
  onEnquire, 
  onClearFilters,
  columns = 3 
}) {
  const gridCols = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  };

  if (loading) {
    return (
      <div className={cn('grid gap-6', gridCols[columns])} data-testid="tours-grid-loading">
        {[...Array(6)].map((_, i) => (
          <PackageSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!tours || tours.length === 0) {
    return <EmptyState onClearFilters={onClearFilters} />;
  }

  return (
    <div className={cn('grid gap-6', gridCols[columns])} data-testid="tours-grid">
      {tours.map((tour) => (
        <TourPackageCard key={tour.id} tour={tour} onEnquire={onEnquire} />
      ))}
    </div>
  );
}
